import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3024c52e-4cee-4083-aea4-e49427d8ac34/artifacts/9fg9a2xl_13921DDF-6D23-4936-90D9-EEE3C5FB5A93.png";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const services = [
    t('services.commercial'),
    t('services.residential'),
    t('services.postConstruction'),
    t('services.moveInOut'),
    t('services.powerWashing'),
    t('services.emergency'),
  ];

  return (
    <footer className="footer py-12 md:py-16" data-testid="footer">
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">
          {/* Company Info - Centered */}
          <div className="text-center flex flex-col items-center">
            <Link to="/" className="inline-block mb-4">
              <img 
                src={LOGO_URL} 
                alt="Gomez Cleaning Service LLC" 
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-[#334155] text-sm leading-relaxed mb-4 max-w-xs">
              {t('footer.description')}
            </p>
            <div className="badge badge-green text-sm">
              {t('footer.licensedBadge')}
            </div>
          </div>

          {/* Services - Centered */}
          <div className="text-center flex flex-col items-center">
            <h4 className="font-semibold text-[#0F172A] mb-4 text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('footer.ourServices')}
            </h4>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service}>
                  <Link 
                    to="/services" 
                    className="text-[#334155] hover:text-[#1E88E5] text-sm transition-colors"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links - Centered */}
          <div className="text-center flex flex-col items-center">
            <h4 className="font-semibold text-[#0F172A] mb-4 text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-[#334155] hover:text-[#1E88E5] text-sm transition-colors">
                  {t('footer.home')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-[#334155] hover:text-[#1E88E5] text-sm transition-colors">
                  {t('footer.about')}
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-[#334155] hover:text-[#1E88E5] text-sm transition-colors">
                  {t('footer.services')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-[#334155] hover:text-[#1E88E5] text-sm transition-colors">
                  {t('footer.contact')}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-[#334155] hover:text-[#1E88E5] text-sm transition-colors">
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-[#334155] hover:text-[#1E88E5] text-sm transition-colors">
                  {t('footer.terms')}
                </Link>
              </li>
            </ul>
            {/* Language Switcher */}
            <div className="mt-6">
              <p className="text-sm text-[#334155] mb-2">{t('footer.language')}:</p>
              <LanguageSwitcher variant="footer" />
            </div>
          </div>

          {/* Contact Info - Centered */}
          <div className="text-center flex flex-col items-center">
            <h4 className="font-semibold text-[#0F172A] mb-4 text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('footer.contactUs')}
            </h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href="tel:+13233780061" 
                  className="flex items-center justify-center gap-2 text-[#334155] hover:text-[#1E88E5] text-sm transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone size={18} className="text-[#1E88E5] flex-shrink-0" />
                  <span>(323) 378-0061</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:gelvia26@gmail.com" 
                  className="flex items-center justify-center gap-2 text-[#334155] hover:text-[#1E88E5] text-sm transition-colors"
                  data-testid="footer-email"
                >
                  <Mail size={18} className="text-[#1E88E5] flex-shrink-0" />
                  <span className="break-all text-xs">gelvia26@gmail.com</span>
                </a>
              </li>
              <li className="flex items-center justify-center gap-2 text-[#334155] text-sm">
                <MapPin size={18} className="text-[#1E88E5] flex-shrink-0" />
                <span>Sylmar, CA 91342</span>
              </li>
              <li className="flex flex-col items-center gap-1 text-[#334155] text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={18} className="text-[#4CAF50] flex-shrink-0" />
                  <span>Mon-Sat: 7AM - 10PM</span>
                </div>
                <p className="text-[#4CAF50] text-xs font-medium">Emergency cleaning available</p>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar - Centered */}
        <div className="divider mb-6"></div>
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="text-[#334155] text-sm">
            © {currentYear} Gomez Cleaning Service LLC. {t('footer.rights')}
          </p>
          <p className="text-[#334155] text-sm">
            {t('footer.serving')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
